/* ============================================================
   PrimeKart Clean White — interaction & animation engine
   GPU-friendly (transform/opacity), no-JS-safe, reduced-motion aware
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(pointer: fine)').matches;

  /* ---------- Scroll reveals + clip-path wipes (IntersectionObserver) ---------- */
  function initReveals() {
    var els = document.querySelectorAll('.pk-reveal, .pk-wipe');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Hero: split headline into words, staggered entrance (v3: keeps .pk-rotator intact) ---------- */
  function initHero() {
    var hero = document.querySelector('.pk-hero');
    if (!hero) return;
    var h1 = hero.querySelector('h1[data-split]');
    if (h1 && !reduceMotion) {
      var label = h1.getAttribute('data-label') || h1.textContent.trim().replace(/\s+/g, ' ');
      h1.setAttribute('aria-label', label);
      var delay = 0.12, out = [];
      Array.prototype.forEach.call(h1.childNodes, function (node) {
        if (node.nodeType === 3) {
          node.textContent.split(/\s+/).forEach(function (w) {
            if (!w) return;
            out.push('<span class="pk-word" aria-hidden="true"><span style="--pk-wd:' + delay.toFixed(2) + 's">' + w + '</span></span>');
            delay += 0.07;
          });
        } else if (node.nodeType === 1) {
          if (node.classList && node.classList.contains('pk-rotator')) {
            out.push('<span class="pk-word" aria-hidden="true"><span style="--pk-wd:' + delay.toFixed(2) + 's">' + node.outerHTML + '</span></span>');
            delay += 0.07;
          } else {
            out.push(node.outerHTML);
          }
        }
      });
      h1.innerHTML = out.join(' ');
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { hero.classList.add('is-in'); });
    });
  }

  /* ---------- Header: hide on scroll down, show on scroll up ---------- */
  function initHeader() {
    var header = document.querySelector('.pk-header');
    if (!header) return;
    var lastY = window.scrollY;
    var ticking = false;
    function onScroll() {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 24);
      if (!reduceMotion) {
        if (y > 320 && y > lastY + 4) {
          header.classList.add('is-hidden');
        } else if (y < lastY - 4 || y <= 320) {
          header.classList.remove('is-hidden');
        }
      }
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(onScroll); ticking = true; }
    }, { passive: true });
    onScroll();

    var toggle = header.querySelector('.pk-menu-toggle');
    var menuDrawer = document.getElementById('PkMenuDrawer');
    if (toggle && menuDrawer) {
      var panel = menuDrawer.querySelector('.pk-drawer__panel');
      // Override panel to slide from left
      panel.style.left = '0'; panel.style.right = 'auto';
      function openMenu() {
        menuDrawer.classList.add('is-open');
        panel.style.transform = 'none';
        toggle.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
      }
      function closeMenu() {
        menuDrawer.classList.remove('is-open');
        panel.style.transform = 'translateX(-105%)';
        toggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      }
      toggle.addEventListener('click', openMenu);
      menuDrawer.querySelectorAll('[data-menu-close]').forEach(function(el){
        el.addEventListener('click', closeMenu);
      });
      document.addEventListener('keydown', function(e){
        if (e.key === 'Escape' && menuDrawer.classList.contains('is-open')) closeMenu();
      });
      // Accordion submenus: tap parent to expand/collapse
      menuDrawer.querySelectorAll('.pk-drawer__accbtn').forEach(function(btn){
        btn.addEventListener('click', function(){
          var acc = btn.closest('.pk-drawer__acc');
          var wasOpen = acc.classList.contains('open');
          menuDrawer.querySelectorAll('.pk-drawer__acc.open').forEach(function(o){
            o.classList.remove('open');
            o.querySelector('.pk-drawer__accbtn').setAttribute('aria-expanded', 'false');
          });
          if (!wasOpen) {
            acc.classList.add('open');
            btn.setAttribute('aria-expanded', 'true');
          }
        });
      });
    } else if (toggle) {
      var nav = header.querySelector('.pk-nav');
      if (nav) {
        toggle.addEventListener('click', function () {
          var open = nav.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }
    }
  }

  /* ---------- Promo banner + hero: subtle parallax ---------- */
  function initParallax() {
    if (reduceMotion || !finePointer) return;
    var items = [];
    document.querySelectorAll('.pk-promo__bg[data-parallax]').forEach(function (bg) {
      items.push({ el: bg, kind: 'promo' });
    });
    document.querySelectorAll('[data-hero-parallax]').forEach(function (img) {
      items.push({ el: img, kind: 'hero' });
    });
    if (!items.length || !('IntersectionObserver' in window)) return;
    var visible = [];
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var i = visible.indexOf(entry.target);
        if (entry.isIntersecting && i === -1) visible.push(entry.target);
        if (!entry.isIntersecting && i !== -1) visible.splice(i, 1);
      });
    });
    items.forEach(function (it) { io.observe(it.el); });
    function targetOf(el) {
      for (var i = 0; i < items.length; i++) if (items[i].el === el) return items[i];
      return null;
    }
    var ticking = false;
    function update() {
      var vh = window.innerHeight;
      visible.forEach(function (el) {
        var t = targetOf(el);
        if (!t) return;
        var host = t.kind === 'hero' ? el.closest('.pk-hero__media') || el : el.parentElement;
        var r = host.getBoundingClientRect();
        var progress = (r.top + r.height / 2 - vh / 2) / vh;
        if (t.kind === 'promo') {
          el.style.transform = 'translate3d(0,' + (progress * -46).toFixed(1) + 'px,0) scale(1.06)';
        } else {
          el.style.transform = 'translate3d(0,' + (progress * 34).toFixed(1) + 'px,0)';
        }
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking && visible.length) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
  }

  /* ---------- Button ripple + satisfying press ---------- */
  function initRipple() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.pk-btn');
      if (!btn || reduceMotion) return;
      var rect = btn.getBoundingClientRect();
      var size = Math.max(rect.width, rect.height);
      var ripple = document.createElement('span');
      ripple.className = 'pk-ripple';
      ripple.style.width = ripple.style.height = size + 'px';
      ripple.style.left = (e.clientX - rect.left - size / 2) + 'px';
      ripple.style.top = (e.clientY - rect.top - size / 2) + 'px';
      btn.appendChild(ripple);
      setTimeout(function () { ripple.remove(); }, 700);
    });
  }

  /* ---------- Product gallery: crossfade + thumbs ---------- */
  function initGallery() {
    var gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;
    var mains = gallery.querySelectorAll('.pk-gallery__main img');
    var thumbs = gallery.querySelectorAll('.pk-gallery__thumb');
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var idx = thumb.getAttribute('data-index');
        mains.forEach(function (img, i) {
          img.classList.toggle('is-active', String(i) === String(idx));
        });
        thumbs.forEach(function (t) { t.classList.toggle('is-active', t === thumb); });
        thumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    });
  }

  /* ---------- Stock bar: animate on scroll into view ---------- */
  function initStockBar() {
    var fills = document.querySelectorAll('.pk-stock__fill[data-stock]');
    if (!fills.length) return;
    function animate(fill) {
      var pct = Math.max(4, Math.min(100, parseFloat(fill.getAttribute('data-stock')) || 70));
      fill.style.width = pct + '%';
    }
    if (reduceMotion || !('IntersectionObserver' in window)) {
      fills.forEach(animate);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { animate(entry.target); io.unobserve(entry.target); }
      });
    }, { threshold: 0.4 });
    fills.forEach(function (f) { io.observe(f); });
  }

  /* ---------- Collapsible tabs ---------- */
  function initTabs() {
    document.querySelectorAll('.pk-tab').forEach(function (tab) {
      var head = tab.querySelector('.pk-tab__head');
      var body = tab.querySelector('.pk-tab__body');
      if (!head || !body) return;
      head.addEventListener('click', function () {
        var open = tab.classList.toggle('is-open');
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
        body.style.maxHeight = open ? body.scrollHeight + 'px' : '0px';
      });
    });
  }

  /* ---------- Quantity stepper ---------- */
  function initQty() {
    document.querySelectorAll('.pk-qty').forEach(function (qty) {
      var input = qty.querySelector('input');
      var minus = qty.querySelector('[data-qty-minus]');
      var plus = qty.querySelector('[data-qty-plus]');
      if (!input) return;
      function set(v) { input.value = Math.max(1, v); }
      if (minus) minus.addEventListener('click', function () { set(parseInt(input.value, 10) - 1 || 1); });
      if (plus) plus.addEventListener('click', function () { set(parseInt(input.value, 10) + 1 || 2); });
    });
  }

  /* ---------- Variant selection (swatches update form, price, stock) ---------- */
  function initSwatches() {
    var dataEl = document.querySelector('[data-pk-variants]');
    if (!dataEl) {
      /* single-variant product: keep visual-only fallback */
      document.querySelectorAll('.pk-swatches').forEach(function (wrap) {
        wrap.querySelectorAll('.pk-swatch').forEach(function (sw) {
          sw.addEventListener('click', function () {
            wrap.querySelectorAll('.pk-swatch').forEach(function (s) {
              s.classList.remove('is-active');
              s.setAttribute('aria-pressed', 'false');
            });
            sw.classList.add('is-active');
            sw.setAttribute('aria-pressed', 'true');
          });
        });
      });
      return;
    }
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!variants.length) return;
    var picker = document.querySelector('[data-pk-variant-picker]');
    var scope = picker ? picker.closest('.pk-container') : document;
    var groups = Array.prototype.slice.call(scope.querySelectorAll('.pk-swatches'));
    if (!groups.length) return;

    function money(cents) {
      var n = (cents / 100).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return 'Rs.' + n;
    }
    function selectedOptions() {
      return groups.map(function (g) {
        var a = g.querySelector('.pk-swatch.is-active');
        return a ? a.getAttribute('data-value') : null;
      });
    }
    function findVariant(sel) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i], ok = true;
        for (var j = 0; j < sel.length; j++) {
          if (v['option' + (j + 1)] !== sel[j]) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }
    function updateVariant(v) {
      if (!v) return;
      /* 1. form variant ids (main product form + sticky ATC bar, which lives outside .pk-container) */
      scope.querySelectorAll('.pk-product__info form[data-ajax-cart] input[name="id"]').forEach(function (inp) {
        inp.value = v.id;
      });
      document.querySelectorAll('#PkStickyAtc form[data-ajax-cart] input[name="id"]').forEach(function (inp) {
        inp.value = v.id;
      });
      /* 2. price */
      var priceBox = scope.querySelector('.pk-product__price');
      if (priceBox) {
        var nowEl = priceBox.querySelector('.pk-now');
        if (nowEl) nowEl.textContent = money(v.price);
        var wasEl = priceBox.querySelector('.pk-was');
        var saveEl = priceBox.querySelector('.pk-save');
        if (v.compare_at_price && v.compare_at_price > v.price) {
          if (!wasEl) {
            wasEl = document.createElement('span');
            wasEl.className = 'pk-was';
            priceBox.appendChild(wasEl);
          }
          wasEl.style.display = '';
          wasEl.textContent = money(v.compare_at_price);
          if (saveEl) {
            saveEl.style.display = '';
            var pct = Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price);
            saveEl.textContent = 'Save ' + pct + '%';
          }
        } else {
          if (wasEl) wasEl.style.display = 'none';
          if (saveEl) saveEl.style.display = 'none';
        }
      }
      /* 3. sticky ATC price */
      var stickyPrice = document.querySelector('#PkStickyAtc .pk-stickyatc__meta span');
      if (stickyPrice) stickyPrice.textContent = money(v.price);
      /* 4. stock indicator */
      var stockBox = scope.querySelector('.pk-stock');
      if (stockBox) {
        var label = stockBox.querySelector('.pk-stock__label');
        var bar = stockBox.querySelector('.pk-stock__bar');
        var fill = stockBox.querySelector('.pk-stock__fill');
        stockBox.classList.remove('pk-stock--urgent');
        if (v.available) {
          if (v.inventory_managed && v.inventory_quantity <= 20) {
            stockBox.classList.add('pk-stock--urgent');
            if (label) label.innerHTML = '<span>\uD83D\uDD25 Hurry! Only ' + v.inventory_quantity + ' left in stock.</span>';
            if (bar) {
              bar.style.display = '';
              if (fill) fill.style.width = Math.max(4, Math.min(92, v.inventory_quantity * 5)) + '%';
            }
          } else {
            if (label) label.innerHTML = '<span>\u2713 In stock</span>';
            if (bar) bar.style.display = 'none';
          }
        } else {
          if (label) label.innerHTML = '<span>Sold out</span>';
          if (bar) bar.style.display = 'none';
        }
      }
      /* 5. buy buttons */
      scope.querySelectorAll('.pk-buy .pk-btn--primary').forEach(function (btn) {
        if (v.available) btn.removeAttribute('disabled'); else btn.setAttribute('disabled', '');
      });
      var stickyBtn = document.querySelector('#PkStickyAtc .pk-btn--primary');
      if (stickyBtn) {
        if (v.available) stickyBtn.removeAttribute('disabled'); else stickyBtn.setAttribute('disabled', '');
      }
    }
    groups.forEach(function (g) {
      g.querySelectorAll('.pk-swatch').forEach(function (sw) {
        sw.addEventListener('click', function () {
          g.querySelectorAll('.pk-swatch').forEach(function (s) {
            s.classList.remove('is-active');
            s.setAttribute('aria-pressed', 'false');
          });
          sw.classList.add('is-active');
          sw.setAttribute('aria-pressed', 'true');
          updateVariant(findVariant(selectedOptions()));
        });
      });
    });
  }

/* ---------- Search drawer ---------- */
  function initSearch() {
    var drawer = document.querySelector('.pk-search');
    if (!drawer) return;
    var openers = document.querySelectorAll('[data-search-open]');
    var input = drawer.querySelector('input[type="search"]');
    function open() {
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (input) setTimeout(function () { input.focus(); }, 60);
    }
    function close() {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    }
    openers.forEach(function (b) { b.addEventListener('click', open); });
    drawer.querySelector('.pk-search__scrim').addEventListener('click', close);
    drawer.querySelector('[data-search-close]').addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  /* ---------- Newsletter: inline success (no backend) ---------- */
  function initNewsletter() {
    document.querySelectorAll('.pk-news__form').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var email = form.querySelector('input[type="email"]');
        if (email && email.checkValidity()) {
          form.closest('.pk-news').classList.add('is-done');
        } else if (email) {
          email.reportValidity();
        }
      });
    });
  }

  /* ---------- AJAX add to cart (v3: fly-to-cart + toast + drawer) ---------- */
  function cartIcon() { return document.querySelector('[data-cart-link]'); }
  function updateCounts(cart) {
    document.querySelectorAll('.pk-cart-count').forEach(function (el) {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });
    var dc = document.querySelector('[data-drawer-count]');
    if (dc) dc.textContent = cart.item_count > 0 ? '(' + cart.item_count + ')' : '';
  }
  function bounceCart() {
    var icon = cartIcon();
    if (!icon || reduceMotion) return;
    icon.classList.remove('is-bounce');
    void icon.offsetWidth;
    icon.classList.add('is-bounce');
  }
  function flyToCart(imgSrc, fromRect) {
    if (reduceMotion || !imgSrc) return;
    var target = cartIcon();
    if (!target || !fromRect) return;
    var t = target.getBoundingClientRect();
    var img = document.createElement('img');
    img.className = 'pk-fly';
    img.src = imgSrc;
    img.alt = '';
    var size = Math.min(120, fromRect.width);
    img.style.cssText += ';width:' + size + 'px;height:' + size + 'px;left:' + fromRect.left + 'px;top:' + fromRect.top + 'px;';
    document.body.appendChild(img);
    var dx = (t.left + t.width / 2) - (fromRect.left + size / 2);
    var dy = (t.top + t.height / 2) - (fromRect.top + size / 2);
    var anim = img.animate([
      { transform: 'translate(0,0) scale(1)', opacity: 1 },
      { transform: 'translate(' + (dx * 0.5).toFixed(0) + 'px,' + (dy - 130).toFixed(0) + 'px) scale(0.55)', opacity: 0.95, offset: 0.55 },
      { transform: 'translate(' + dx.toFixed(0) + 'px,' + dy.toFixed(0) + 'px) scale(0.15)', opacity: 0.3 }
    ], { duration: 750, easing: 'cubic-bezier(0.22,0.61,0.36,1)' });
    anim.onfinish = function () { img.remove(); bounceCart(); };
  }
  function findProductImage(form) {
    var card = form.closest('.pk-card');
    if (card) { var ci = card.querySelector('.pk-card__media img'); if (ci) return ci; }
    var gal = document.querySelector('.pk-gallery__main img.is-active') || document.querySelector('.pk-gallery__main img');
    if (form.closest('.pk-stickyatc') && gal) return gal;
    if (gal && form.closest('.pk-product')) return gal;
    return null;
  }
  function initAjaxCart() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[data-ajax-cart]');
      if (!form) return;
      e.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      var original = btn ? btn.innerHTML : '';
      var pimg = findProductImage(form);
      var rect = pimg ? pimg.getBoundingClientRect() : null;
      var src = pimg ? (pimg.currentSrc || pimg.src) : null;
      if (btn) { btn.disabled = true; btn.innerHTML = 'Adding…'; }
      fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { 'Accept': 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(function () {
          flyToCart(src, rect);
          return fetch('/cart.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          updateCounts(cart);
          showToast('Added to cart ✓');
          renderDrawer(cart);
          if (btn) { btn.innerHTML = '✓ Added'; setTimeout(function () { btn.innerHTML = original; btn.disabled = false; }, 1600); }
          setTimeout(openDrawer, reduceMotion ? 0 : 650);
        })
        .catch(function () {
          form.submit();
        });
    });
  }

  /* ---------- Scroll progress bar + back-to-top with progress ring ---------- */
  function initProgress() {
    var bar = document.createElement('div');
    bar.className = 'pk-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    var top = document.getElementById('PkBackTop');
    var ring = top ? top.querySelector('.pk-top__ring') : null;
    var CIRC = 138.2;
    var ticking = false;
    function update() {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      if (!reduceMotion) {
        bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
        if (ring) ring.style.strokeDashoffset = (CIRC * (1 - p)).toFixed(1);
      } else if (ring) {
        ring.style.strokeDashoffset = 0;
      }
      if (top) top.classList.toggle('is-show', window.scrollY > 600);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
    if (top) {
      top.addEventListener('click', function () {
        window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    }
  }

  /* ---------- Product cards: subtle 3D tilt on hover ---------- */
  function initTilt() {
    if (reduceMotion || !finePointer) return;
    document.querySelectorAll('.pk-card').forEach(function (card) {
      var raf = null;
      card.addEventListener('mousemove', function (e) {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          var r = card.getBoundingClientRect();
          var px = (e.clientX - r.left) / r.width - 0.5;
          var py = (e.clientY - r.top) / r.height - 0.5;
          card.classList.add('is-tilting');
          card.style.transform = 'translateY(-6px) rotateX(' + (-py * 7).toFixed(2) + 'deg) rotateY(' + (px * 9).toFixed(2) + 'deg)';
          raf = null;
        });
      });
      card.addEventListener('mouseleave', function () {
        card.classList.remove('is-tilting');
        card.style.transform = '';
      });
    });
  }

  /* ---------- Magnetic buttons: gentle pull toward cursor ---------- */
  function initMagnetic() {
    if (reduceMotion || !finePointer) return;
    document.querySelectorAll('[data-magnetic]').forEach(function (btn) {
      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        var dx = e.clientX - (r.left + r.width / 2);
        var dy = e.clientY - (r.top + r.height / 2);
        btn.style.transform = 'translate(' + (dx * 0.14).toFixed(1) + 'px,' + (dy * 0.22).toFixed(1) + 'px)';
      });
      btn.addEventListener('mouseleave', function () {
        btn.style.transform = '';
      });
    });
  }

  /* ---------- Animated stat counters ---------- */
  function initCounters() {
    var nums = document.querySelectorAll('[data-count]');
    if (!nums.length) return;
    function fmt(n) {
      return n >= 1000 ? n.toLocaleString('en-US') : String(n);
    }
    function run(el) {
      var end = parseInt(el.getAttribute('data-count'), 10) || 0;
      if (reduceMotion) { el.textContent = fmt(end); return; }
      var t0 = null, dur = 1500;
      function step(t) {
        if (!t0) t0 = t;
        var p = Math.min(1, (t - t0) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = fmt(Math.round(end * eased));
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }
    if (reduceMotion || !('IntersectionObserver' in window)) {
      nums.forEach(run);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { run(entry.target); io.unobserve(entry.target); }
      });
    }, { threshold: 0.5 });
    nums.forEach(function (n) { io.observe(n); });
  }


  /* ================= v3.0 — maximum animation modules ================= */

  /* ---------- 1. Brand preloader (skip on repeat visits) ---------- */
  function initPreloader() {
    var loader = document.getElementById('PkLoader');
    if (!loader) return;
    if (reduceMotion) { loader.style.display = 'none'; return; }
    /* v4.47: branded white overlay stays until FULL page load on every visit,
       so it covers third-party spinners (e.g. Trustoo). Fades via .pk-done. */
    loader.classList.add('pk-loading');
    function done() {
      loader.classList.add('pk-done');
      setTimeout(function () { loader.style.display = 'none'; }, 600);
    }
    var finished = false;
    function finishOnce() { if (!finished) { finished = true; done(); } }
    if (document.readyState === 'complete') {
      finishOnce();
    } else {
      window.addEventListener('load', finishOnce);
    }
    setTimeout(finishOnce, 8000); /* safety net */
  }

  /* ---------- 2a. Rotating headline words ---------- */
  function initRotator() {
    var rot = document.querySelector('[data-rotate]');
    if (!rot || reduceMotion) return;
    var words = rot.querySelectorAll('.pk-rotator__w');
    if (words.length < 2) return;
    var i = 0;
    setInterval(function () {
      var cur = words[i];
      i = (i + 1) % words.length;
      var next = words[i];
      cur.classList.add('is-leaving');
      cur.classList.remove('is-active');
      setTimeout(function () {
        cur.classList.remove('is-leaving');
        next.classList.add('is-active');
      }, 420);
    }, 2800);
  }

  /* ---------- 2b. Hero cursor spotlight (desktop only) ---------- */
  function initHeroGlow() {
    if (reduceMotion || !finePointer) return;
    var hero = document.querySelector('.pk-hero');
    var glow = document.getElementById('PkHeroGlow');
    if (!hero || !glow) return;
    var tx = 0, ty = 0, x = 0, y = 0, raf = null;
    function loop() {
      x += (tx - x) * 0.12; y += (ty - y) * 0.12;
      glow.style.left = x + 'px'; glow.style.top = y + 'px';
      if (Math.abs(tx - x) > 0.5 || Math.abs(ty - y) > 0.5) raf = requestAnimationFrame(loop);
      else raf = null;
    }
    hero.addEventListener('mousemove', function (e) {
      var r = hero.getBoundingClientRect();
      tx = e.clientX - r.left; ty = e.clientY - r.top;
      if (!raf) raf = requestAnimationFrame(loop);
    });
  }

  /* ---------- 3. Custom cursor ---------- */
  function initCursor() {
    return; /* superseded by cursor v3 — kept for reference */
    if (reduceMotion || !finePointer) return;
    var dot = document.getElementById('PkCursor');
    var ring = document.getElementById('PkCursorRing');
    if (!dot || !ring) return;
    document.documentElement.classList.add('pk-cursor-on');
    var mx = -100, my = -100, rx = -100, ry = -100;
    document.addEventListener('mousemove', function (e) {
      mx = e.clientX; my = e.clientY;
      dot.style.transform = 'translate(' + (mx - 4) + 'px,' + (my - 4) + 'px)';
    });
    (function ringLoop() {
      rx += (mx - rx) * 0.16; ry += (my - ry) * 0.16;
      var half = ring.classList.contains('is-hover') ? 26 : 17;
      ring.style.transform = 'translate(' + (rx - half) + 'px,' + (ry - half) + 'px)';
      requestAnimationFrame(ringLoop);
    })();
    document.addEventListener('mouseover', function (e) {
      ring.classList.toggle('is-hover', !!e.target.closest('a, button, input, .pk-card, [data-magnetic]'));
    });
  }

  /* ---------- Toast ---------- */
  function showToast(msg) {
    var toast = document.getElementById('PkToast');
    if (!toast) return;
    toast.querySelector('.pk-toast__msg').textContent = msg;
    toast.setAttribute('aria-hidden', 'false');
    toast.classList.add('is-show');
    clearTimeout(toast._t);
    toast._t = setTimeout(function () {
      toast.classList.remove('is-show');
      toast.setAttribute('aria-hidden', 'true');
    }, 2600);
  }

  /* ---------- 5. Cart drawer ---------- */
  function drawer() { return document.getElementById('PkCartDrawer'); }
  function openDrawer() {
    var d = drawer();
    if (!d) return;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    var d = drawer();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
  function money(cents) {
    try { return (cents / 100).toLocaleString('en-PK', { style: 'currency', currency: 'PKR', maximumFractionDigits: 0 }); }
    catch (e) { return 'Rs. ' + (cents / 100); }
  }
  function renderDrawer(cart) {
    var d = drawer();
    if (!d) return;
    var box = d.querySelector('[data-drawer-items]');
    var foot = d.querySelector('[data-drawer-foot]');
    updateCounts(cart);
    if (!cart.items.length) {
      box.innerHTML = '<div class="pk-drawer__empty">Your cart is empty.</div>';
      if (foot) foot.style.display = 'none';
      return;
    }
    if (foot) foot.style.display = '';
    box.innerHTML = cart.items.map(function (it, i) {
      return '<div class="pk-drawer__row" style="--pk-d:' + (i * 0.06).toFixed(2) + 's">' +
        '<img src="' + it.image.replace(/(\.[a-z]+)(\?|$)/, '_160x$1$2') + '" alt="" loading="lazy">' +
        '<div><h4>' + it.title + '</h4>' +
        '<div class="pk-drawer__qty">Qty: ' + it.quantity + '</div>' +
        '<button class="pk-drawer__rm" data-remove="' + it.key + '">Remove</button></div>' +
        '<div class="pk-drawer__price">' + money(it.final_line_price) + '</div></div>';
    }).join('');
    var sub = d.querySelector('[data-drawer-subtotal]');
    if (sub) sub.textContent = money(cart.total_price);
    box.querySelectorAll('[data-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: btn.getAttribute('data-remove'), quantity: 0 })
        }).then(function (r) { return r.json(); })
          .then(function (c) { renderDrawer(c); })
          .catch(function () { window.location.href = '/cart'; });
      });
    });
  }
  function initCartDrawer() {
    var d = drawer();
    if (!d) return;
    d.querySelectorAll('[data-drawer-close]').forEach(function (el) {
      el.addEventListener('click', closeDrawer);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && d.classList.contains('is-open')) closeDrawer();
    });
    var link = document.querySelector('[data-cart-link]');
    if (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        fetch('/cart.js').then(function (r) { return r.json(); })
          .then(function (cart) { renderDrawer(cart); openDrawer(); })
          .catch(function () { window.location.href = '/cart'; });
      });
    }
  }

  /* ---------- 6. Announcement message rotator ---------- */
  function initAnnounceRotate() {
    var bar = document.querySelector('[data-rotate-msgs]');
    if (!bar || reduceMotion) return;
    var msgs = bar.querySelectorAll('.pk-announce__msg');
    if (msgs.length < 2) return;
    var i = 0, ms = parseInt(bar.getAttribute('data-rotate-msgs'), 10) || 4000;
    setInterval(function () {
      var cur = msgs[i];
      i = (i + 1) % msgs.length;
      var next = msgs[i];
      cur.classList.add('is-leaving');
      cur.classList.remove('is-active');
      setTimeout(function () {
        cur.classList.remove('is-leaving');
        next.classList.add('is-active');
      }, 400);
    }, ms);
  }

  /* ---------- 8. Char-by-char section title reveal ---------- */
  function initCharReveal() {
    return; /* DISABLED v3.8: caused word-flash glitch on scroll; headings use simple .pk-reveal fade instead */
    var heads = document.querySelectorAll('.pk-heading h2, .pk-coll-head h1');
    if (!heads.length) return;
    heads.forEach(function (h) {
      if (h.querySelector('.pk-char') || reduceMotion) return;
      var text = h.textContent;
      h.setAttribute('aria-label', text.trim());
      var html = '', ci = 0;
      text.split('').forEach(function (ch) {
        if (ch === ' ') { html += ' '; return; }
        html += '<span class="pk-char" aria-hidden="true" style="--pk-cd:' + (ci * 0.022).toFixed(3) + 's">' + (ch === '<' ? '&lt;' : ch) + '</span>';
        ci++;
      });
      h.innerHTML = html;
    });
  }

  /* ---------- 10. Sticky add-to-cart bar ---------- */
  function initStickyAtc() {
    var bar = document.getElementById('PkStickyAtc');
    if (!bar || !('IntersectionObserver' in window)) return;
    var buyBtn = document.querySelector('.pk-buy [type="submit"]');
    if (!buyBtn) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-show', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        document.body.classList.toggle('pk-atc-visible', show);
      });
    });
    io.observe(buyBtn);
  }

  /* ---------- Thumb active indicator slide ---------- */
  function initThumbIndicator() {
    var wrap = document.querySelector('[data-thumbs]');
    if (!wrap || reduceMotion) return;
    var ind = wrap.querySelector('.pk-thumbs__indicator');
    var thumbs = wrap.querySelectorAll('.pk-gallery__thumb');
    if (!ind || !thumbs.length) return;
    function place(active) {
      ind.style.width = active.offsetWidth + 'px';
      ind.style.transform = 'translateX(' + active.offsetLeft + 'px)';
    }
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { place(t); });
    });
    place(wrap.querySelector('.pk-gallery__thumb.is-active') || thumbs[0]);
    window.addEventListener('resize', function () {
      var a = wrap.querySelector('.pk-gallery__thumb.is-active') || thumbs[0];
      place(a);
    });
  }

  /* ---------- 11. Testimonial slider (autoplay + drag + dots) ---------- */
  function initSlider() {
    var slider = document.querySelector('[data-slider]');
    if (!slider) return;
    var track = slider.querySelector('.pk-slider__track');
    var slides = slider.querySelectorAll('.pk-slider__slide');
    var dotsBox = slider.querySelector('[data-slider-dots]');
    if (slides.length < 2) {
      var nav = slider.querySelector('.pk-slider__nav');
      if (nav) nav.style.display = 'none';
      return;
    }
    var i = 0, timer = null, dragging = false, startX = 0, curX = 0;
    slides.forEach(function (_, n) {
      var b = document.createElement('button');
      b.className = 'pk-slider__dot' + (n === 0 ? ' is-active' : '');
      b.setAttribute('aria-label', 'Go to review ' + (n + 1));
      b.addEventListener('click', function () { go(n); restart(); });
      dotsBox.appendChild(b);
    });
    var dots = dotsBox.querySelectorAll('.pk-slider__dot');
    function go(n) {
      i = (n + slides.length) % slides.length;
      track.style.transform = 'translateX(-' + (i * 100) + '%)';
      dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
    }
    function restart() {
      if (reduceMotion) return;
      clearInterval(timer);
      timer = setInterval(function () { go(i + 1); }, 5200);
    }
    var prev = slider.querySelector('[data-slider-prev]');
    var next = slider.querySelector('[data-slider-next]');
    if (prev) prev.addEventListener('click', function () { go(i - 1); restart(); });
    if (next) next.addEventListener('click', function () { go(i + 1); restart(); });
    var viewport = slider.querySelector('.pk-slider__viewport');
    viewport.addEventListener('pointerdown', function (e) {
      dragging = true; startX = e.clientX; curX = 0;
      track.style.transition = 'none';
      viewport.setPointerCapture(e.pointerId);
    });
    viewport.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      curX = e.clientX - startX;
      track.style.transform = 'translateX(calc(-' + (i * 100) + '% + ' + curX + 'px))';
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      track.style.transition = '';
      if (curX < -60) go(i + 1);
      else if (curX > 60) go(i - 1);
      else go(i);
      restart();
    }
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    slider.addEventListener('mouseenter', function () { clearInterval(timer); });
    slider.addEventListener('mouseleave', restart);
    restart();
  }

  /* ---------- 13. Image shimmer skeletons ---------- */
  function initShimmer() {
    if (reduceMotion) return;
    document.querySelectorAll('.pk-card__media, .pk-gallery__main, .pk-hero__media').forEach(function (box) {
      var img = box.querySelector('img');
      if (!img) return;
      function clear() { box.classList.remove('pk-media--loading'); }
      if (img.complete && img.naturalWidth > 0) return;
      box.classList.add('pk-media--loading');
      img.addEventListener('load', clear, { once: true });
      img.addEventListener('error', clear, { once: true });
      setTimeout(clear, 6000);
    });
  }

  /* ---------- 16. Sort chips: sliding pill + grid re-cascade ---------- */
  function initSortChips() {
    var wrap = document.querySelector('[data-sortchips]');
    if (!wrap) return;
    var pill = wrap.querySelector('.pk-sortchips__pill');
    var chips = wrap.querySelectorAll('.pk-sortchip');
    var active = wrap.querySelector('.pk-sortchip.is-active') || chips[0];
    function place(el) {
      if (!pill || !el) return;
      pill.style.width = el.offsetWidth + 'px';
      pill.style.transform = 'translateX(' + (el.offsetLeft - 5) + 'px)';
    }
    place(active);
    window.addEventListener('resize', function () {
      place(wrap.querySelector('.pk-sortchip.is-active') || chips[0]);
    });
    chips.forEach(function (chip) {
      chip.addEventListener('click', function (e) {
        if (reduceMotion) return;
        e.preventDefault();
        var grid = document.querySelector('[data-coll-grid]');
        chips.forEach(function (c) { c.classList.remove('is-active'); });
        chip.classList.add('is-active');
        place(chip);
        if (grid) grid.classList.add('is-switching');
        setTimeout(function () { window.location.href = chip.href; }, 260);
      });
    });
  }

  /* ================= end v3 modules ================= */

  /* ---------- Boot ---------- */
  document.addEventListener('DOMContentLoaded', function () {
    initPreloader();
    initReveals();
    initCharReveal();
    initHero();
    initRotator();
    initHeroGlow();
    initCursor();
    initHeader();
    initParallax();
    initRipple();
    initGallery();
    initThumbIndicator();
    initStockBar();
    initTabs();
    initQty();
    initSwatches();
    initSearch();
    initNewsletter();
    initAjaxCart();
    initCartDrawer();
    initStickyAtc();
    initAnnounceRotate();
    initSlider();
    initShimmer();
    initSortChips();
    initProgress();
    initTilt();
    initMagnetic();
    initCounters();
  });
})();

/* ============================================================
   FINAL ANIMATION LAYER (v3.1) — cursor v2, confetti, scramble
   Runs as a separate IIFE; never touches the v3 engine above.
   ============================================================ */
;(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) return; /* all five features disabled */

  var finePointer = window.matchMedia('(pointer: fine)').matches;

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------- 1. Custom cursor v2: glowing orb + sparkles + VIEW pill ---------- */
  function initCursorV2() {
    return; /* superseded by cursor v3 — kept for reference */
    if (!finePointer) return;
    var orb = document.createElement('div');
    orb.className = 'pk-cursor2';
    orb.id = 'PkCursor2';
    orb.setAttribute('aria-hidden', 'true');
    document.body.appendChild(orb);
    document.documentElement.classList.add('pk-cursor2-on');

    var mx = -100, my = -100, ox = -100, oy = -100, half = 8;
    document.addEventListener('mousemove', function (e) {
      mx = e.clientX; my = e.clientY;
      spawnSparkle(mx, my);
    }, { passive: true });

    (function loop() {
      ox += (mx - ox) * 0.22; oy += (my - oy) * 0.22;
      half = orb.classList.contains('is-view') ? 19 : (orb.classList.contains('is-hover') ? 22 : 8);
      orb.style.transform = 'translate(' + (ox - half) + 'px,' + (oy - half) + 'px)';
      requestAnimationFrame(loop);
    })();

    var lastSpark = 0, activeSparks = 0;
    function spawnSparkle(x, y) {
      var now = performance.now();
      if (now - lastSpark < 55 || activeSparks > 24) return;
      lastSpark = now; activeSparks++;
      var s = document.createElement('div');
      s.className = 'pk-spark';
      var off = (Math.random() - 0.5) * 18;
      s.style.left = (x + off) + 'px';
      s.style.top = (y + off) + 'px';
      document.body.appendChild(s);
      var dx = (Math.random() - 0.5) * 46, dy = -14 - Math.random() * 26;
      var anim = s.animate([
        { transform: 'translate(0,0) scale(1)', opacity: 0.9 },
        { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(0)', opacity: 0 }
      ], { duration: 480 + Math.random() * 260, easing: 'cubic-bezier(.2,.7,.3,1)' });
      anim.onfinish = function () { s.remove(); activeSparks--; };
    }

    function syncState(e) {
      var t = e.target;
      var onCard = !!(t.closest && t.closest('.pk-card'));
      var onHit = !!(t.closest && t.closest('a, button, input, select, textarea, [data-magnetic]'));
      orb.classList.toggle('is-view', onCard);
      orb.classList.toggle('is-hover', !onCard && onHit);
    }
    document.addEventListener('mouseover', syncState, { passive: true });
  }

  /* ---------- 2. Confetti burst on add-to-cart ---------- */
  var lastAtcBtn = null;
  document.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('form[data-ajax-cart] [type="submit"]') : null;
    if (b) lastAtcBtn = b;
  }, true);

  function confettiBurst(x, y) {
    var colors = ['#0B6B4A', '#C9A227', '#ffffff', '#139a6c', '#0B6B4A', '#e9b949'];
    var bits = [];
    for (var i = 0; i < 24; i++) {
      var el = document.createElement('div');
      el.className = 'pk-confetti-bit';
      var size = 5 + Math.random() * 6;
      el.style.width = size + 'px';
      el.style.height = (Math.random() < 0.5 ? size : size * 0.45) + 'px';
      el.style.background = colors[i % colors.length];
      el.style.borderRadius = Math.random() < 0.4 ? '50%' : '2px';
      document.body.appendChild(el);
      var ang = -Math.PI / 2 + (Math.random() - 0.5) * 1.9;
      var sp = 4 + Math.random() * 7;
      bits.push({ el: el, x: x, y: y, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp, rot: Math.random() * 360, vr: (Math.random() - 0.5) * 14, life: 0 });
    }
    var frames = 0;
    (function tick() {
      frames++;
      var alive = false;
      for (var i = 0; i < bits.length; i++) {
        var b = bits[i];
        if (b.life > 75) continue;
        alive = true;
        b.life++;
        b.vy += 0.32; b.vx *= 0.985; b.rot += b.vr;
        b.x += b.vx; b.y += b.vy;
        var fade = b.life > 52 ? Math.max(0, 1 - (b.life - 52) / 23) : 1;
        b.el.style.transform = 'translate(' + b.x + 'px,' + b.y + 'px) rotate(' + b.rot + 'deg)';
        b.el.style.opacity = fade.toFixed(3);
        if (b.life > 75) b.el.remove();
      }
      if (alive) requestAnimationFrame(tick);
    })();
  }

  function initConfetti() {
    var toast = document.getElementById('PkToast');
    if (!toast) return;
    var mo = new MutationObserver(function () {
      var msg = toast.querySelector('.pk-toast__msg');
      if (toast.classList.contains('is-show') && msg && /added to cart/i.test(msg.textContent)) {
        var x = window.innerWidth / 2, y = window.innerHeight - 140;
        if (lastAtcBtn && document.contains(lastAtcBtn)) {
          var r = lastAtcBtn.getBoundingClientRect();
          x = r.left + r.width / 2; y = r.top + r.height / 2;
        }
        confettiBurst(x, y);
      }
    });
    mo.observe(toast, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------- 3. Scramble-text hero reveal (once) ---------- */
  function initScramble() {
    return; /* DISABLED: conflicts with split-text reveal, caused persistent glyph overlap */
    var h1 = document.querySelector('.pk-hero h1');
    if (!h1) return;
    var glyphs = '!<>-_\\/[]{}=+*^?#';
    var nodes = [];
    Array.prototype.forEach.call(h1.childNodes, function (n) {
      if (n.nodeType === 3 && /[^\s]/.test(n.nodeValue)) nodes.push(n);
    });
    if (!nodes.length) return;
    setTimeout(function () {
      nodes.forEach(function (node, ni) {
        var orig = node.nodeValue, len = orig.length, frame = 0;
        var total = 26 + ni * 8;
        (function scramble() {
          frame++;
          var out = '';
          for (var i = 0; i < len; i++) {
            var ch = orig[i];
            if (/\s/.test(ch)) { out += ch; continue; }
            var revealAt = (i / len) * total;
            out += frame >= revealAt ? ch : glyphs[(Math.random() * glyphs.length) | 0];
          }
          node.nodeValue = out;
          if (frame < total + 2) requestAnimationFrame(scramble);
          else node.nodeValue = orig;
        })();
      });
    }, 1050);
  }

  onReady(function () {
    initCursorV2();
    initConfetti();
    initScramble();
    /* features 4 (gradient bar) & 5 (pulsing badges) are pure CSS */
  });
})();

(function () {
  'use strict';
  var d=document, w=window;
  var reduceMotion = w.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = w.matchMedia('(pointer: fine)').matches;
  function onReady(fn) {
    if (d.readyState !== 'loading') fn();
    else d.addEventListener('DOMContentLoaded', fn);
  }
  function initCur3() {
    return; /* superseded by star cursor v3.6 */
    if (!finePointer || reduceMotion) return;
    d.documentElement.classList.add('pk-cursor3-on');
    var dot = d.createElement('div'); dot.className = 'pk-cursor3-dot';
    var ring = d.createElement('div'); ring.className = 'pk-cursor3-ring';
    d.body.appendChild(dot);
    d.body.appendChild(ring);
    var mx = -100, my = -100, dx = -100, dy = -100, rx = -100, ry = -100, raf = 0;
    function loop() {
      dx += (mx - dx) * 0.42; dy += (my - dy) * 0.42;
      rx += (mx - rx) * 0.16; ry += (my - ry) * 0.16;
      dot.style.transform = 'translate(' + (dx - 4) + 'px,' + (dy - 4) + 'px)';
      ring.style.transform = 'translate(' + rx + 'px,' + ry + 'px) translate(-50%,-50%)';
      raf = requestAnimationFrame(loop);
    }
    d.addEventListener('mousemove', function (e) {
      mx = e.clientX; my = e.clientY;
      if (!raf) loop();
    }, { passive: true });
    d.addEventListener('mouseleave', function () { if (raf) { cancelAnimationFrame(raf); raf = 0; } });
    d.addEventListener('mouseenter', function () { if (!raf) loop(); });
    function ripple(x, y) {
      var r = d.createElement('div');
      r.className = 'pk-ripple';
      r.style.left = x + 'px'; r.style.top = y + 'px';
      r.style.width = '90px'; r.style.height = '90px';
      d.body.appendChild(r);
      r.addEventListener('animationend', function h() { r.removeEventListener('animationend', h); r.remove(); });
      setTimeout(function () { if (r.parentNode) r.remove(); }, 700);
    }
    d.addEventListener('mousedown', function (e) { ripple(e.clientX, e.clientY); }, { passive: true });
    function sync(e) {
      var t = e.target;
      var onCard = !!(t.closest && t.closest('.pk-card'));
      var onHit = !!(t.closest && t.closest('a, button, input, select, textarea, [data-magnetic]'));
      ring.classList.toggle('is-view', onCard);
      ring.classList.toggle('is-hover', !onCard && onHit);
    }
    d.addEventListener('mouseover', sync, { passive: true });
  }
  function initWipe() {
    /* PERMANENTLY REMOVED 2026-10-11 */
  }
  var atcB = null;
  d.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('form[data-ajax-cart] [type="submit"]') : null;
    if (b) atcB = b;
  }, true);
  function morphB(btn) {
    if (!btn || !d.contains(btn) || btn.classList.contains('pk-atc-morphing')) return;
    var r = btn.getBoundingClientRect();
    btn.style.width = r.width + 'px';
    btn.style.height = r.height + 'px';
    var check = d.createElement('span');
    check.className = 'pk-atc-check';
    check.setAttribute('aria-hidden', 'true');
    check.innerHTML = '<svg viewBox="0 0 24 24"><path d="M4.5 12.5l5 5 10-11"/></svg>';
    btn.appendChild(check);
    void btn.offsetWidth;
    btn.classList.add('pk-atc-morphing');
    requestAnimationFrame(function () { btn.style.width = Math.max(r.height, 44) + 'px'; });
    setTimeout(function () {
      btn.classList.remove('pk-atc-morphing');
      btn.style.width = ''; btn.style.height = '';
      if (check.parentNode) check.parentNode.removeChild(check);
    }, 1700);
  }
  function initMorph() {
    var toast = d.getElementById('PkToast');
    if (!toast) return;
    var mo = new MutationObserver(function () {
      var msg = toast.querySelector('.pk-toast__msg');
      if (toast.classList.contains('is-show') && msg && /added to cart/i.test(msg.textContent)) {
        morphB(atcB);
      }
    });
    mo.observe(toast, { attributes: true, attributeFilter: ['class'] });
  }
  function initBlur() {
    var imgs = d.querySelectorAll('.pk-card img, .pk-hero__media img');
    if (!imgs.length) return;
    function sharpen(img) { img.classList.add('is-sharp'); }
    Array.prototype.forEach.call(imgs, function (img) {
      if (img.classList.contains('pk-blurup')) return;
      img.classList.add('pk-blurup');
      if (img.complete && img.naturalWidth > 0) { sharpen(img); return; }
      var done = false;
      function h() { if (!done) { done = true; sharpen(img); } }
      img.addEventListener('load', h);
      img.addEventListener('error', h);
      setTimeout(h, 4000);
    });
  }
  function initWave() {
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    var grids = d.querySelectorAll('.pk-grid');
    if (!grids.length) return;
    Array.prototype.forEach.call(grids, function (grid) {
      var cards = grid.children;
      if (!cards.length) return;
      var cols = 4;
      try {
        var n = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
        if (n > 0) cols = n;
      } catch (e) {}
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
            var card = entry.target;
            setTimeout(function () {
              card.classList.remove('pk-wave');
              card.style.transitionDelay = '';
            }, 1400);
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -30px 0px' });
      Array.prototype.forEach.call(cards, function (card, i) {
        card.classList.add('pk-wave');
        card.style.transitionDelay = ((i % cols) * 60) + 'ms';
        io.observe(card);
      });
    });
  }
  onReady(function () {
    initCur3();
    initWipe();
    initMorph();
    initBlur();
    initWave();
  });
})();
/* ============ BEAUTY LAYER (v3.3): aurora backdrop + hero bokeh ============ */
(function(){
  'use strict';
  if(!('matchMedia' in window))return;
  if(window.matchMedia('(prefers-reduced-motion: reduce)').matches)return;

  /* 1. Aurora background — fixed dreamy tints behind the whole page */
  try{
    if(!document.querySelector('.pk-aurora')){
      var aurora=document.createElement('div');
      aurora.className='pk-aurora';
      aurora.setAttribute('aria-hidden','true');
      for(var i=0;i<4;i++){aurora.appendChild(document.createElement('i'));}
      document.body.appendChild(aurora);
    }
  }catch(e){}

  /* 2. Bokeh particles in hero — generated once */
  try{
    var hero=document.querySelector('.pk-hero');
    if(hero&&!hero.querySelector('.pk-bokeh')){
      for(var j=0;j<14;j++){
        var s=document.createElement('span');
        s.className='pk-bokeh';
        s.setAttribute('aria-hidden','true');
        hero.appendChild(s);
      }
    }
  }catch(e){}
})();

/* ===== STAR SPARKLE CURSOR (v3.6) ===== */
(function () {
  'use strict';
  var d = document, w = window;
  var reduceMotion = w.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = w.matchMedia('(pointer: fine)').matches;
  function onReady(fn) { if (d.readyState !== 'loading') fn(); else d.addEventListener('DOMContentLoaded', fn); }

  /* PK-GALLERY-TOUCH 2026-10-11: swipe + pinch-zoom + pan for product images */
function initGalleryTouch() {
  var galleries = document.querySelectorAll('[data-gallery]');
  if (!galleries.length) return;
  galleries.forEach(function(gal) {
    var main = gal.querySelector('.pk-gallery__main');
    var thumbs = gal.querySelectorAll('.pk-gallery__thumb');
    var imgs = gal.querySelectorAll('.pk-gallery__main img');
    if (!main || !imgs.length) return;
    var idx = 0, scale = 1, tx = 0, ty = 0;
    var startX = 0, startY = 0, startDist = 0, startScale = 1;
    var panning = false, pinch = false, swiped = false;
    var dblTapTimer = null;

    function show(i) {
      idx = (i + imgs.length) % imgs.length;
      imgs.forEach(function(im, j) { im.classList.toggle('is-active', j === idx); });
      thumbs.forEach(function(th, j) { th.classList.toggle('is-active', j === idx); });
      resetZoom();
    }
    function resetZoom() {
      scale = 1; tx = 0; ty = 0;
      applyTransform();
    }
    function applyTransform() {
      var im = imgs[idx];
      if (!im) return;
      im.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
      im.style.cursor = scale > 1 ? 'grab' : '';
    }
    function getDist(t1, t2) {
      return Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
    }

    // Thumbnail clicks (existing behavior)
    thumbs.forEach(function(th) {
      th.addEventListener('click', function() {
        show(parseInt(th.dataset.index, 10) || 0);
      });
    });

    // Touch: swipe / pinch / pan
    main.addEventListener('touchstart', function(e) {
      if (e.touches.length === 2) {
        pinch = true; panning = false; swiped = false;
        startDist = getDist(e.touches[0], e.touches[1]);
        startScale = scale;
      } else if (e.touches.length === 1) {
        startX = e.touches[0].clientX; startY = e.touches[0].clientY;
        panning = scale > 1; swiped = false;
      }
    }, {passive: true});

    main.addEventListener('touchmove', function(e) {
      if (pinch && e.touches.length === 2) {
        e.preventDefault();
        var d = getDist(e.touches[0], e.touches[1]);
        scale = Math.min(4, Math.max(1, startScale * d / startDist));
        if (scale === 1) { tx = 0; ty = 0; }
        applyTransform();
      } else if (panning && e.touches.length === 1) {
        e.preventDefault();
        tx += e.touches[0].clientX - startX;
        ty += e.touches[0].clientY - startY;
        // clamp pan within reasonable bounds
        var lim = 120 * scale;
        tx = Math.max(-lim, Math.min(lim, tx));
        ty = Math.max(-lim, Math.min(lim, ty));
        startX = e.touches[0].clientX; startY = e.touches[0].clientY;
        applyTransform();
        swiped = true;
      } else if (e.touches.length === 1 && scale === 1) {
        var dx = e.touches[0].clientX - startX;
        if (Math.abs(dx) > 12) swiped = true;
      }
    }, {passive: false});

    main.addEventListener('touchend', function(e) {
      if (pinch) { pinch = false; panning = scale > 1; return; }
      if (!swiped && scale === 1 && e.changedTouches.length === 1) {
        var dx = e.changedTouches[0].clientX - startX;
        var dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          show(idx + (dx < 0 ? 1 : -1));
          return;
        }
        // double-tap to zoom
        var now = Date.now();
        if (dblTapTimer && now - dblTapTimer < 300) {
          scale = scale > 1 ? 1 : 2.2; tx = 0; ty = 0;
          applyTransform(); dblTapTimer = null;
        } else { dblTapTimer = now; }
      }
      panning = false;
    });

    // Desktop: wheel to zoom, drag to pan
    main.addEventListener('wheel', function(e) {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        scale = Math.min(4, Math.max(1, scale - Math.sign(e.deltaY) * 0.25));
        if (scale === 1) { tx = 0; ty = 0; }
        applyTransform();
      }
    }, {passive: false});
  });
}

  function initSimpleCursor() {
    if (!finePointer || reduceMotion) return;
    d.documentElement.classList.add('pk-gcur-on');

    // Click ripple animation (real green arrow cursor comes from CSS)
    d.addEventListener('mousedown', function (e) {
      var rip = d.createElement('div');
      rip.className = 'pk-gcur-ripple';
      rip.style.left = e.clientX + 'px';
      rip.style.top = e.clientY + 'px';
      d.body.appendChild(rip);
      setTimeout(function () { rip.remove(); }, 600);
    });
  }

  onReady(initGalleryTouch);
  onReady(initSimpleCursor);
})();
